import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Bell, AlertTriangle, CheckCircle2 } from "lucide-react";
import AppLayout from "@/components/AppLayout";
import PageHeader from "@/components/PageHeader";
import KpiCard from "@/components/KpiCard";
import DataTable, { Column } from "@/components/DataTable";
import { LoadingState } from "@/components/LoadingState";
import SeverityBadge from "@/components/SeverityBadge";
import { staffViewsApi } from "@/api/endpoints";

interface ARow {
  id: string;
  alert_type: string;
  severity: "low" | "medium" | "high";
  message: string;
  is_resolved: boolean;
  created_at: string;
  organisation_id: string;
  organisation_name: string;
  meter_number: string;
  building_name: string;
  unit_number: string;
}

export default function StaffAlertsPage() {
  const [rows, setRows] = useState<ARow[]>([]);
  const [loading, setLoading] = useState(true);
  const [unresolved, setUnresolved] = useState(true);

  useEffect(() => {
    setLoading(true);
    staffViewsApi.alerts(unresolved).then((r) => { setRows(r); setLoading(false); });
  }, [unresolved]);

  const high = rows.filter((r) => !r.is_resolved && r.severity === "high").length;
  const open = rows.filter((r) => !r.is_resolved).length;

  const columns: Column<ARow>[] = [
    { key: "message", header: "Message", render: (a) => <span className="font-medium">{a.message}</span> },
    { key: "type", header: "Type", render: (a) => <span className="badge bg-slate-100 text-slate-700">{a.alert_type.replace("_", " ")}</span> },
    { key: "sev", header: "Severity", render: (a) => <SeverityBadge severity={a.severity} /> },
    {
      key: "org", header: "Organisation",
      render: (a) => <Link to={`/staff/organisations/${a.organisation_id}`} className="text-brand-700 hover:underline">{a.organisation_name}</Link>,
    },
    {
      key: "loc", header: "Location",
      render: (a) => <span className="text-xs text-slate-600">{a.building_name} · Unit {a.unit_number} · {a.meter_number}</span>,
    },
    { key: "when", header: "Created", render: (a) => new Date(a.created_at).toLocaleString("en-ZA") },
  ];

  return (
    <AppLayout>
      <PageHeader
        title="Alerts across all organisations"
        subtitle="System-wide alert monitoring"
        actions={
          <label className="flex items-center gap-2 text-sm text-slate-600">
            <input type="checkbox" checked={unresolved} onChange={(e) => setUnresolved(e.target.checked)} />
            Unresolved only
          </label>
        }
      />
      {loading ? <LoadingState /> : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
            <KpiCard label="Open alerts" value={open} icon={<Bell size={18} />} accent={open ? "red" : "brand"} />
            <KpiCard label="High severity" value={high} icon={<AlertTriangle size={18} />} accent={high ? "red" : "brand"} />
            <KpiCard label="Showing" value={rows.length} icon={<CheckCircle2 size={18} />} accent="slate" />
          </div>
          <DataTable columns={columns} rows={rows} emptyMessage="No alerts" />
        </>
      )}
    </AppLayout>
  );
}
